import React from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Paper, Switch, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { PageContainer, SectionHeader, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import { gs, Loader, StaleHint, ensureOptions, ensureRegions, setRegionsCache, canEditRegions, Cut, UsageBar, FIXED, ELLIPSIS, MONO,
         ago, when, n, locationText } from "./servers";

// Game Servers → Regions and Providers (§12, §13, §16). A NETWORK region (where machines run — never a map region) is a
// dev-defined row of the global `gsRegions`: its countries (the fallback for a player with no ping) and its ordered
// provider locations (plans, the Vultr DDoS add-on, a fixed beacon host). Regions are global, so editing them takes the
// `servers` AND `admin` roles (`region-add` / `region-update` / `region-remove`); the live machines per region come from
// `overview`. A provider ACCOUNT (`gsProviders`) is read-only here (`providers`): kind, enabled, credential status.

const REGION_ID = /^[a-z0-9-]{1,32}$/;
const CODE = /^[A-Za-z0-9._-]{1,64}$/;
const HOST = /^[A-Za-z0-9.:-]{1,253}$/;
const STATUS = { ok: "success", error: "error", disabled: "default", "not-built": "default" };
const splitList = (v) => String(v ?? "").split(/[\s,]+/).map(x => x.trim()).filter(Boolean);
const updatedCol = { id: "updated", label: "Updated", width: 120, render: r => <Tooltip title={`${when(r.updatedDate)}${r.updatedBy ? ` by ${r.updatedBy}` : ""}`}>
    <Box sx={ELLIPSIS}>{ago(r.updatedDate)}{r.updatedBy ? ` · ${r.updatedBy}` : ""}</Box></Tooltip> };

// "vultr:sao · vhp-1c-2gb-amd · DDoS"
function LocationChips({ locations }) {
    if (!(locations ?? []).length) return <Typography variant="body2" color="error.main">no location</Typography>;
    return <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
        {locations.map((l, i) => {
            const plans = (l.plans ?? []).join(", ") || "any plan";
            return <Tooltip key={`${l.provider}:${l.code}`} title={`${i + 1}. ${locationText(l)} · plans: ${plans}${l.ddosAddon ? " · Vultr DDoS add-on at create" : ""}${l.beacon ? ` · beacon ${l.beacon}` : ""}`}>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", minWidth: 0 }}>
                    <Box component="span" sx={{ ...MONO, fontWeight: 600, flexShrink: 0 }}>{i + 1}. {locationText(l)}</Box>
                    <Box component="span" sx={{ ...ELLIPSIS, fontSize: 12, color: "text.secondary" }}>{plans}</Box>
                    {l.ddosAddon && <Chip size="small" variant="outlined" color="info" label="DDoS" sx={{ height: 18, fontSize: 11 }} />}
                </Box>
            </Tooltip>;
        })}
    </Box>;
}

// ===================== Regions =====================
export class RegionsPage extends React.Component {
    state = { netRegions: null, overview: null, providers: null, options: null, optionsError: null, loadedAt: null, stale: null, dialog: null, busy: false };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        const [regions, overview, providers] = await Promise.all([
            gs("Loading regions", ["region-list"], { silent: true }),
            gs("Loading the overview", ["overview"], { silent: true }).catch(() => null),
            this.state.providers ? null : gs("Loading providers", ["providers"], { silent: true }).catch(() => null),
        ]);
        setRegionsCache(regions);
        return { netRegions: regions, ...(overview ? { overview } : {}), ...(providers ? { providers: providers.providers ?? [] } : {}) };
    }, 10000);

    componentDidMount() { this.loader.start(); }
    componentWillUnmount() { this.loader.stop(); }

    live = (id) => (this.state.overview?.regions ?? []).find(r => r.region === id) ?? null;
    applyAnswer = (r) => { if (r?.regions) { setRegionsCache(r); this.setState({ netRegions: r }); } };

    // ----- dialog -----
    blankLocation = () => ({ provider: this.state.providers?.find(p => p.canCreate && p.enabled)?.id ?? "vultr", code: "", plans: "", ddosAddon: false, beacon: "" });
    openAdd = () => this.setState({ dialog: { mode: "add", id: "", name: "", enabled: true, countries: "", locations: [this.blankLocation()] } });
    openEdit = (r) => this.setState({ dialog: { mode: "edit", id: r.id, name: r.name ?? "", enabled: r.enabled !== false, countries: (r.countries ?? []).join(", "),
        locations: (r.locations ?? []).map(l => ({ provider: l.provider ?? "", code: l.code ?? "", plans: (l.plans ?? []).join(", "), ddosAddon: !!l.ddosAddon, beacon: l.beacon ?? "" })) } });
    setDialog = (patch) => this.setState(st => ({ dialog: { ...st.dialog, ...patch } }));
    setLocation = (i, patch) => this.setDialog({ locations: this.state.dialog.locations.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
    moveLocation = (i, d) => {
        const l = [...this.state.dialog.locations];
        const j = i + d;
        if (j < 0 || j >= l.length) return;
        [l[i], l[j]] = [l[j], l[i]];
        this.setDialog({ locations: l });
    };
    removeLocation = (i) => this.setDialog({ locations: this.state.dialog.locations.filter((_, j) => j !== i) });

    dialogError = () => {
        const d = this.state.dialog;
        if (d.mode === "add") {
            if (!REGION_ID.test(d.id)) return "The id is a-z, 0-9 and -, at most 32 characters (e.g. br, us, us-west).";
            if (d.id === "local") return "local is the region of developers' launchers.";
            if ((this.state.netRegions?.regions ?? []).some(r => r.id === d.id)) return `Region ${d.id} exists already.`;
        }
        if (!d.name.trim()) return "A region needs a name.";
        const bad = splitList(d.countries).find(c => !/^[A-Za-z]{2}$/.test(c));
        if (bad) return `"${bad}" is not an ISO-2 country code (BR, US…).`;
        if (d.locations.length === 0) return "A region needs at least one location.";
        const seen = new Set();
        for (const [i, l] of d.locations.entries()) {
            if (!l.provider.trim()) return `Location ${i + 1}: pick a provider.`;
            if (!CODE.test(l.code.trim())) return `Location ${i + 1}: the provider's location code (e.g. sao, ord, ewr).`;
            if (l.beacon.trim() && !HOST.test(l.beacon.trim())) return `Location ${i + 1}: the beacon is a host name or an IP.`;
            const key = `${l.provider.trim()}:${l.code.trim()}`;
            if (seen.has(key)) return `Location ${key} is listed twice.`;
            seen.add(key);
        }
        return "";
    };
    save = () => {
        const d = this.state.dialog;
        const body = {
            name: d.name.trim(), enabled: !!d.enabled, countries: splitList(d.countries).map(c => c.toUpperCase()),
            locations: d.locations.map(l => ({ provider: l.provider.trim(), code: l.code.trim(), plans: splitList(l.plans), ddosAddon: !!l.ddosAddon, beacon: l.beacon.trim() || null })),
        };
        const [desc, cmd] = d.mode === "add" ? ["Adding the region", "region-add"] : ["Saving the region", "region-update"];
        this.setState({ busy: true });
        gs(desc, [cmd], { payload: { id: d.id, ...body }, retry: false })
            .then(r => { this.applyAnswer(r); this.setState({ dialog: null }); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };
    remove = (r) => {
        const live = this.live(r.id);
        if (!window.confirm(`Remove region ${r.id} (${r.name})?\n\n` + (live?.machines
            ? `It has ${live.machines} machine(s): the backend refuses while a machine, fleet or live session is in the region or a title lists it.`
            : "The backend refuses while a fleet or live session is in the region or a title lists it (GameServersConfig.regions)."))) return;
        gs("Removing the region", ["region-remove", r.id], { retry: false }).then(this.applyAnswer).catch(() => {});
    };

    renderLocation(l, i, count) {
        const { providers, options } = this.state;
        const kind = providers?.find(p => p.id === l.provider)?.kind ?? (l.provider === "vultr" ? "vultr" : null);
        const vultrCodes = kind === "vultr" ? (options?.regions ?? []) : [];
        const providerOptions = (providers ?? []).map(p => ({ value: p.id, label: `${p.id} (${p.kind}${p.enabled ? "" : ", disabled"})` }));
        if (l.provider && !providerOptions.some(o => o.value === l.provider)) providerOptions.push({ value: l.provider, label: l.provider });
        const plansHint = kind === "vultr" && l.code
            ? (options?.plans ?? []).filter(p => (p.locations ?? []).includes(l.code.trim())).length
            : null;
        return <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ width: 22 }}>{i + 1}.</Typography>
                {providerOptions.length > 0
                    ? <SelectField label="Provider" size="small" minWidth={170} value={l.provider} onChange={v => this.setLocation(i, { provider: v })} options={providerOptions} />
                    : <TextField label="Provider" size="small" sx={{ width: 170 }} value={l.provider} onChange={e => this.setLocation(i, { provider: e.target.value })} />}
                <Autocomplete freeSolo size="small" sx={{ flex: 1 }} options={vultrCodes.map(r => r.id)} value={l.code}
                              renderOption={(props, id) => {
                                  const { key, ...rest } = props;
                                  const r = vultrCodes.find(x => x.id === id);
                                  return <li key={key} {...rest}>{id}{r ? ` · ${r.city}, ${r.country} (${r.continent})` : ""}</li>;
                              }}
                              onInputChange={(_, v) => this.setLocation(i, { code: v ?? "" })}
                              renderInput={params => <TextField {...params} label="Location code" />} />
                <Tooltip title="Earlier in the order"><span><IconButton size="small" disabled={i === 0} onClick={() => this.moveLocation(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Later in the order"><span><IconButton size="small" disabled={i === count - 1} onClick={() => this.moveLocation(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Remove the location"><IconButton size="small" color="error" onClick={() => this.removeLocation(i)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, pl: "30px" }}>
                <TextField label="Plans (comma separated; empty = any)" size="small" sx={{ flex: 1 }} value={l.plans} onChange={e => this.setLocation(i, { plans: e.target.value })}
                           helperText={plansHint != null ? `${plansHint} Vultr plan(s) offered at ${l.code.trim()}` : " "} />
                <TextField label="Beacon host (optional)" size="small" sx={{ width: 200 }} value={l.beacon} onChange={e => this.setLocation(i, { beacon: e.target.value })}
                           helperText="UDP echo when no machine is ready" />
                <Tooltip title="Vultr's paid DDoS protection (USD 10 / month per instance), requested at create: for the always-on base machines">
                    <FormControlLabel sx={{ mb: 2.5 }} label="DDoS add-on" control={<Checkbox size="small" checked={l.ddosAddon} onChange={e => this.setLocation(i, { ddosAddon: e.target.checked })} />} />
                </Tooltip>
            </Box>
        </Paper>;
    }

    renderDialog() {
        const { dialog: d, busy, optionsError } = this.state;
        if (!d) return null;
        const err = this.dialogError();
        const editing = d.mode === "edit";
        return <FormDialog open title={editing ? `Edit region ${d.id}` : "Add a network region"} onClose={() => this.setState({ dialog: null })} onConfirm={this.save}
                           confirmText={editing ? "Save" : "Add"} confirmDisabled={!!err || busy} maxWidth="md" fullWidth>
            {optionsError && <Alert severity="warning" sx={{ mb: 1 }}>Vultr's location list is unavailable ({optionsError}): type the location codes.</Alert>}
            <Box sx={{ display: "flex", gap: 2, mt: 1, alignItems: "center" }}>
                <TextField label="Id" sx={{ width: 160 }} value={d.id} disabled={editing} onChange={e => this.setDialog({ id: e.target.value.toLowerCase() })}
                           slotProps={{ htmlInput: { style: { fontFamily: "ui-monospace, Menlo, monospace" } } }} />
                <TextField label="Name (shown to players: Brazil, United States…)" sx={{ flex: 1 }} value={d.name} onChange={e => this.setDialog({ name: e.target.value })} />
                <FormControlLabel label="Enabled" control={<Switch checked={d.enabled} onChange={e => this.setDialog({ enabled: e.target.checked })} />} />
            </Box>
            <TextField label="Countries (ISO-2, comma separated)" fullWidth sx={{ mt: 2 }} value={d.countries} onChange={e => this.setDialog({ countries: e.target.value })}
                       helperText="The geo fallback: a player with no ping report yet is placed by the country of their account." />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, mb: 1 }}>
                <Typography variant="subtitle2">Locations, in order</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => this.setDialog({ locations: [...d.locations, this.blankLocation()] })}>Add location</Button>
            </Box>
            {d.locations.map((l, i) => this.renderLocation(l, i, d.locations.length))}
            {err && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>{err}</Typography>}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                A hand deploy goes to the first location whose provider can create and that offers the plan; the scaler walks the list in
                order (reserved machines are filled first), and a failing location is skipped for the rule's backoff. Saving replaces the
                whole location list.{!editing && " A new region needs a scale rule (Scaling page) before machines appear in it on their own."}
            </Typography>
        </FormDialog>;
    }

    render() {
        const { netRegions, loadedAt, stale } = this.state;
        const edit = canEditRegions();
        const regions = netRegions?.regions ?? [];
        const titleRegions = netRegions?.titleRegions ?? [];
        const columns = [
            { id: "region", label: "Region", width: 150, render: r => <>
                <Box sx={{ ...ELLIPSIS, fontWeight: 600 }}>{r.id} · {r.name}</Box>
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                    {r.enabled === false && <Chip size="small" variant="outlined" label="disabled" />}
                    {titleRegions.includes(r.id) && <Tooltip title={`This title plays here (GameServersConfig.regions, position ${titleRegions.indexOf(r.id) + 1})`}><Chip size="small" color="primary" variant="outlined" label={`title #${titleRegions.indexOf(r.id) + 1}`} /></Tooltip>}
                    {netRegions?.defaultRegion === r.id && <Tooltip title="The title's default region: hand deploys, worlds and players with no data go here"><Chip size="small" color="success" variant="outlined" label="default" /></Tooltip>}
                </Box>
              </> },
            { id: "countries", label: "Countries", width: 120, render: r => <Cut text={(r.countries ?? []).join(", ")} /> },
            { id: "locations", label: "Locations (order) · plans", width: 290, render: r => <LocationChips locations={r.locations} /> },
            { id: "beacons", label: "Beacons", width: 120, render: r => <Cut text={(r.locations ?? []).map(l => l.beacon).filter(Boolean).join(", ")} sx={MONO} /> },
            { id: "machines", label: "Machines ready / total", width: 95, align: "right", render: r => { const l = this.live(r.id); return l ? `${n(l.ready)} / ${n(l.machines)}` : "0"; } },
            { id: "capacity", label: "Units used / total", width: 100, align: "right", render: r => { const l = this.live(r.id); return l ? <>{n(l.used)} / {n(l.capacity)}<UsageBar used={l.used} total={l.capacity} /></> : "—"; } },
            { id: "players", label: "Servers · players", width: 95, align: "right", render: r => { const l = this.live(r.id); return l ? `${n(l.servers)} · ${n(l.players)}` : "—"; } },
            updatedCol,
            ...(edit ? [{ id: "actions", label: "", width: 80, render: r => <Box sx={{ display: "flex", gap: 0.25 }}>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => this.openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => this.remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box> }] : []),
        ];
        // machines in a region id the table does not know (rows not migrated yet, local)
        const unknown = (this.state.overview?.regions ?? []).filter(o => !regions.some(r => r.id === o.region));
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Regions">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    {edit && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openAdd}>Add region</Button>}
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2">
                        A <strong>network region</strong> is where machines run (never a map region). Regions are ours to define and work
                        independently: their own scale rules, machines, world shards and instance numbers. Players are placed by their ping to
                        each region's beacon (the client reports it after login), else by their country, else the title's default region.
                        Regions are global (every title sees the same rows); a title plays in the ones its <code>GameServersConfig.regions</code> lists
                        {titleRegions.length > 0 ? <> — this title: <strong>{titleRegions.join(", ")}</strong>{netRegions?.defaultRegion ? <>, default <strong>{netRegions.defaultRegion}</strong></> : null}</> : null}.
                        {!edit && " Editing takes both the servers and the admin role on this title."}
                    </Typography>
                </Paper>
                {netRegions == null
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the regions: ${stale}` : "Loading…"}</Typography>
                    : regions.length === 0
                        ? <Typography variant="body2" color="text.secondary">No region yet.</Typography>
                        : <DataTable columns={columns} rows={regions} getRowKey={r => r.id} sx={FIXED} />}
                {unknown.length > 0 && <Alert severity={unknown.some(u => u.region !== "local") ? "warning" : "info"} sx={{ mt: 2 }}>
                    Machines in region ids the table does not list: {unknown.map(u => `${u.region} (${u.machines})`).join(", ")}.
                    {unknown.some(u => u.region !== "local") ? " Rows of before the regions carry a provider code (sao, mia, ewr): `servers regions-migrate` maps them to their region." : " local = developers' launchers."}
                </Alert>}
                {this.renderDialog()}
            </PageContainer>
        );
    }
}

// ===================== Providers =====================
export class ProvidersPage extends React.Component {
    state = { providers: null, netRegions: null, loadedAt: null, stale: null };

    loader = new Loader(this, async () => {
        ensureRegions(this);
        const r = await gs("Loading providers", ["providers"], { silent: true });
        return { providers: r?.providers ?? [] };
    }, 30000);

    componentDidMount() { this.loader.start(); }
    componentWillUnmount() { this.loader.stop(); }

    render() {
        const { providers, netRegions, loadedAt, stale } = this.state;
        const usedBy = (id) => (netRegions?.regions ?? []).filter(r => (r.locations ?? []).some(l => l.provider === id))
            .map(r => `${r.id} (${(r.locations ?? []).filter(l => l.provider === id).map(l => l.code).join(", ")})`);
        const columns = [
            { id: "id", label: "Account", width: 110, render: p => <Box sx={{ ...ELLIPSIS, fontWeight: 600 }}>{p.id}</Box> },
            { id: "kind", label: "Kind", width: 90, render: p => <Cut text={p.kind} /> },
            { id: "enabled", label: "Enabled", width: 75, render: p => p.enabled ? <Chip size="small" color="success" variant="outlined" label="yes" /> : <Chip size="small" variant="outlined" label="no" /> },
            { id: "create", label: "Creates machines", width: 85, render: p => p.canCreate
                ? <Chip size="small" variant="outlined" label="API" />
                : <Tooltip title="Never created or destroyed by the backend: machines are adopted (Machines → Adopt machine)"><Chip size="small" variant="outlined" label="adopt only" /></Tooltip> },
            { id: "status", label: "Status", width: 150, render: p => <Box sx={{ minWidth: 0 }}>
                <Chip size="small" color={STATUS[p.status] ?? "default"} variant={STATUS[p.status] === "default" || !STATUS[p.status] ? "outlined" : "filled"} label={p.status ?? "?"} />
                {p.error && <Cut text={p.error} sx={{ color: "error.main", fontSize: 12, mt: 0.25 }} />}
              </Box> },
            { id: "endpoint", label: "Endpoint", width: 150, render: p => <Cut text={p.endpoint} sx={MONO} /> },
            { id: "credentials", label: "Credentials file", width: 140, render: p => <Cut text={p.credentials} sx={MONO} /> },
            { id: "project", label: "Project", width: 95, render: p => <Cut text={p.projectId} sx={MONO} /> },
            { id: "machines", label: "Machines", width: 70, align: "right", render: p => n(p.machines) },
            { id: "regions", label: "Used by regions", width: 150, render: p => <Cut text={usedBy(p.id).join(", ")} /> },
            { id: "note", label: "Note", width: 160, render: p => <Cut text={p.note} /> },
        ];
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Providers">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The machine provider ACCOUNTS (<code>gsProviders</code>, global): a region's location names one. Kinds: <strong>vultr</strong> (hourly
                    VPS, created and destroyed through the API), <strong>reserved</strong> (monthly machines — dedicated or anything else — that are
                    adopted, filled first and never destroyed by the backend) and <strong>ovh-cloud</strong> (not built yet: it waits for the OVHcloud
                    US account). The status comes from the provider's cached lists, never from a create. Read-only for now: accounts are seeded by
                    the backend.
                </Typography>
                {providers == null
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the providers: ${stale}` : "Loading…"}</Typography>
                    : providers.length === 0
                        ? <Typography variant="body2" color="text.secondary">No provider account.</Typography>
                        : <DataTable columns={columns} rows={providers} getRowKey={p => p.id} sx={FIXED} />}
            </PageContainer>
        );
    }
}
